import { Controller } from "@hotwired/stimulus"
require("daterangepicker/daterangepicker.css");
require("select2/dist/css/select2.min.css");

// requires jQuery, moment, might want to consider a vanilla JS alternative
import $ from 'jquery';
import 'daterangepicker';
import moment from 'moment';
import select2 from "select2/dist/js/select2.full.js";
export default class extends Controller {
  static targets = [ "field", "clearButton", "currentTimeZoneWrapper", "timeZoneButtons", "timeZoneSelectWrapper", "timeZoneField", "precision", "anchor", "wallClockReference", "offset", "duration", "buffer", "count", "preview", "details", "compactControl", "combinedSelect", "dateToggle", "dateControl", "bufferControl", "bufferAction", "precisionControl", "precisionAction", "precisionActionLabel" ]
  static values = {
    includeDate: { type: Boolean, default: true },
    includeTime: Boolean,
    defaultTimeZones: Array,
    cancelButtonLabel: { type: String, default: "Cancel" },
    applyButtonLabel: { type: String, default: "Apply" },
    defaultDayPrecision: { type: String, default: "PT86400S" },
    defaultTimedPrecision: { type: String, default: "PT900S" },
    translations: Object,
    parentAnchor: String,
    parentOffset: String,
    parentPrecision: String,
    parentCount: String,
    parentPoint: String,
    parentReferenceUtc: String,
    parentTotalStartUtc: String,
    parentTotalEndUtc: String,
    wallClockReference: String,
    parentTimeZone: String
  }

  // Add a property to store the current precision in seconds
  currentPrecisionInSeconds = 0;

  initialize() {
    if (window.jQuery === undefined) {
      window.jQuery = $;
      window.$ = $;
    }
    if (!window.jQuery.fn?.select2) {
      select2();
    }
    this.boundBeforeSubmit = this.beforeSubmit.bind(this);
  }

  get hasPrecisionSelectTarget() {
    return this.hasPrecisionTarget && this.precisionTarget.matches('select');
  }

  connect() {
    this.lastAnchorValue = this.hasAnchorTarget ? this.anchorTarget.value : '';
    this.bufferExpanded = false;
    this.precisionExpanded = false;
    this.formElement = this.element.closest('form');
    this.handleCombinedSelectKeydown = this.handleCombinedSelectKeydown.bind(this);
    this.handleCombinedSelectKeyup = this.handleCombinedSelectKeyup.bind(this);
    this.consumeCombinedSelectEscapeKeyup = false;
    document.addEventListener('keydown', this.handleCombinedSelectKeydown, true);
    document.addEventListener('keyup', this.handleCombinedSelectKeyup, true);
    if (this.formElement) {
      this.formElement.addEventListener('submit', this.boundBeforeSubmit);
    }

    this.initPluginInstance()
    if (this.hasPrecisionSelectTarget) {
      this.populatePrecisionOptions()
      this.currentPrecisionInSeconds = this.getPrecisionSeconds(); // Initialize after first population

      $(this.precisionTarget).on('change.timeline', () => {
        const previousOffsetSeconds = this.hasOffsetTarget ? this.durationToSeconds($(this.offsetTarget).val() || 'PT0S') : 0;
        const previousDurationCount = this.hasDurationTarget ? parseFloat($(this.durationTarget).val() || '0') : 0;
        const previousBufferCount = this.hasBufferTarget ? parseFloat($(this.bufferTarget).val() || '0') : 0;
        const wasCompactDayPicker = this.currentPrecisionInSeconds >= 24 * 3600 &&
          previousOffsetSeconds === 0 &&
          previousDurationCount === 1 &&
          previousBufferCount === 0;

        // Calculate target actual seconds using the *old* (current) precision
        const targetDurationSeconds = previousDurationCount * this.currentPrecisionInSeconds;
        const targetBufferSeconds = previousBufferCount * this.currentPrecisionInSeconds;

        // Update stored precision to the new value from the select
        this.currentPrecisionInSeconds = this.getPrecisionSeconds();

        const nextDurationSeconds = wasCompactDayPicker && this.currentPrecisionInSeconds < 24 * 3600
          ? this.currentPrecisionInSeconds
          : targetDurationSeconds;
        const nextBufferSeconds = wasCompactDayPicker && this.currentPrecisionInSeconds < 24 * 3600
          ? 0
          : targetBufferSeconds;

        // Repopulate dependent selects
        if (this.hasOffsetTarget) this.populateOffsetOptions();
        if (this.hasDurationTarget) this.populateDurationOptions(nextDurationSeconds);
        if (this.hasBufferTarget) this.populateBufferOptions(nextBufferSeconds);
        this.updateDetailsVisibility();
        this.updateDisclosureVisibility();
        this.renderPreview(); // Rerender preview on precision change
      })
    }
    if (this.hasOffsetTarget) {
      this.populateOffsetOptions()
      $(this.offsetTarget).on('change.timeline', () => {
        this.handleOffsetChange.bind(this)();
        this.updateDetailsVisibility();
        this.updateDisclosureVisibility();
        this.renderPreview(); // Rerender preview on offset change
      })
    }
    if (this.hasDurationTarget) {
      this.populateDurationOptions()
      $(this.durationTarget).on('change.timeline', () => {
        this.updateCount.bind(this)();
        if (this.hasBufferTarget) this.populateBufferOptions();
        this.updateDetailsVisibility();
        this.updateDisclosureVisibility();
        this.renderPreview(); // Rerender preview on duration change
      })
    }
    if (this.hasBufferTarget) {
      this.populateBufferOptions()
      $(this.bufferTarget).on('change.timeline', () => {
        this.updateCount.bind(this)();
        this.bufferExpanded = this.bufferHasValue();
        this.updateDetailsVisibility();
        this.updateDisclosureVisibility();
        this.renderPreview(); // Rerender preview on buffer change
      })
    }
    this.initCombinedSelects();
    this.updateDateVisibility();
    this.updateDetailsVisibility();
    this.updateDisclosureVisibility();
    this.syncTimelineRemovalState();
    this.renderPreview(); // Initial render
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleCombinedSelectKeydown, true);
    document.removeEventListener('keyup', this.handleCombinedSelectKeyup, true);
    if (this.formElement) {
      this.formElement.removeEventListener('submit', this.boundBeforeSubmit);
    }
    this.teardownCombinedSelects();
    if (this.hasPrecisionTarget) {
      $(this.precisionTarget).off('.timeline')
    }
    if (this.hasOffsetTarget) {
      $(this.offsetTarget).off('.timeline')
    }
    if (this.hasDurationTarget) {
      $(this.durationTarget).off('.timeline')
    }
    if (this.hasBufferTarget) {
      $(this.bufferTarget).off('.timeline')
    }
    this.teardownPluginInstance()
  }

  initCombinedSelects() {
    if (!this.hasCombinedSelectTarget || !window.jQuery.fn?.select2) return;

    this.combinedSelectTargets.forEach((select) => {
      const selectElement = $(select);
      if (selectElement.data('select2')) return;

      selectElement.select2({
        width: '100%',
        minimumResultsForSearch: 0,
        dropdownParent: $(select.closest('.timeline-combined-select') || this.element),
        dropdownCssClass: 'timeline-combined-dropdown',
        templateSelection: (option) => this.combinedSelectSelection(option)
      });

      selectElement.on('select2:close.timelineDisclosure', () => {
        if (this.hasBufferTarget && select === this.bufferTarget && !this.bufferHasValue()) {
          this.bufferExpanded = false;
        }
        if (this.hasPrecisionTarget && select === this.precisionTarget) {
          this.precisionExpanded = false;
        }
        this.updateDisclosureVisibility();
      });
    });
  }

  teardownCombinedSelects() {
    if (!this.hasCombinedSelectTarget || !window.jQuery.fn?.select2) return;

    this.combinedSelectTargets.forEach((select) => {
      const selectElement = $(select);
      selectElement.off('.timelineDisclosure');
      if (selectElement.data('select2')) {
        selectElement.select2('destroy');
      }
    });
  }

  updateCount() {
    if (!this.hasCountTarget || !this.hasDurationTarget || !this.hasBufferTarget) return;

    const duration = parseFloat($(this.durationTarget).val()) || 0;
    const buffer = parseFloat($(this.bufferTarget).val()) || 0;

    // Update the count range [duration, duration + buffer]
    const countValue = `[${duration},${duration + buffer}]`;
    this.countTarget.value = countValue;
  }

  shouldHideDetails() {
    if (!this.hasDetailsTarget) return false;

    const precisionSeconds = this.getPrecisionSeconds();
    if (precisionSeconds < 24 * 3600) return false;

    const offsetSeconds = this.hasOffsetTarget ? this.durationToSeconds($(this.offsetTarget).val() || 'PT0S') : 0;
    const durationCount = this.hasDurationTarget ? parseFloat($(this.durationTarget).val() || '1') : 1;
    const bufferCount = this.hasBufferTarget ? parseFloat($(this.bufferTarget).val() || '0') : 0;

    return offsetSeconds === 0 && durationCount === 1 && bufferCount === 0;
  }

  updateDetailsVisibility() {
    const hideDetails = this.shouldHideDetails();
    if (this.hasDetailsTarget) {
      this.detailsTargets.forEach((target) => {
        target.classList.toggle('hidden', hideDetails);
      });
    }
    if (this.hasCompactControlTarget) {
      this.compactControlTargets.forEach((target) => {
        target.classList.toggle('hidden', !hideDetails);
      });
    }
    this.updateDisclosureVisibility();
    this.syncTimelineRemovalState();
  }

  updateDisclosureVisibility() {
    this.updatePrecisionActionLabel();

    const canToggleBuffer = this.hasBufferActionTarget;
    const showBufferControl = !canToggleBuffer || this.bufferExpanded || this.bufferHasValue();
    this.bufferControlTargets.forEach((target) => {
      target.classList.toggle('hidden', !showBufferControl);
    });
    this.bufferActionTargets.forEach((target) => {
      target.classList.toggle('hidden', showBufferControl);
    });

    const canTogglePrecision = this.hasPrecisionActionTarget;
    const showPrecisionControl = !canTogglePrecision || this.precisionExpanded;
    this.precisionControlTargets.forEach((target) => {
      target.classList.toggle('hidden', !showPrecisionControl);
    });
    this.precisionActionTargets.forEach((target) => {
      target.classList.toggle('hidden', showPrecisionControl);
    });
  }

  updatePrecisionActionLabel() {
    if (!this.hasPrecisionActionLabelTarget || !this.hasPrecisionTarget) return;

    const selectedOption = this.precisionTarget.options[this.precisionTarget.selectedIndex];
    const precisionLabel = selectedOption?.text || this.translationsValue?.selects?.precision?.label || 'Precision';
    const label = this.precisionActionLabel(precisionLabel);
    this.precisionActionLabelTargets.forEach((target) => {
      target.textContent = label;
    });
  }

  precisionActionLabel(precisionLabel) {
    const template = this.translationsValue?.precision?.action_label;
    if (!template || !precisionLabel) return precisionLabel;

    return template.replace(/%\{precision\}/g, precisionLabel);
  }

  combinedSelectSelection(option) {
    return option.element?.dataset?.timelineSelectionLabel || option.text;
  }

  bufferSelectionLabel(bufferLabel) {
    const template = this.translationsValue?.selects?.buffer?.selection_label;
    if (!template || !bufferLabel) return bufferLabel;

    return template.replace(/%\{buffer\}/g, bufferLabel);
  }

  formatBufferSelectionValue(count, precisionSeconds) {
    const totalSeconds = count * precisionSeconds;
    const units = this.translationsValue?.selects?.buffer?.selection_units || {};
    const formatUnit = (unit, value) => (units[unit] || `%{count}${unit[0]}`).replace(/%\{count\}/g, value);

    if (totalSeconds < 60) return formatUnit('seconds', totalSeconds);
    if (totalSeconds < 3600) return formatUnit('minutes', Math.floor(totalSeconds / 60));
    if (totalSeconds < 86400) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return [formatUnit('hours', hours), minutes > 0 ? formatUnit('minutes', minutes) : null]
        .filter(Boolean)
        .join(' ');
    }

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    return [formatUnit('days', days), hours > 0 ? formatUnit('hours', hours) : null]
      .filter(Boolean)
      .join(' ');
  }

  bufferHasValue() {
    if (!this.hasBufferTarget) return false;

    return (parseFloat($(this.bufferTarget).val() || '0') || 0) > 0;
  }

  showBuffer(event) {
    if (event) event.preventDefault();
    if (!this.hasBufferTarget) return;

    this.bufferExpanded = true;
    this.updateDisclosureVisibility();
    this.openCombinedSelect(this.bufferTarget);
  }

  showPrecision(event) {
    if (event) event.preventDefault();
    if (!this.hasPrecisionSelectTarget) return;

    this.precisionExpanded = true;
    this.updateDisclosureVisibility();
    this.openCombinedSelect(this.precisionTarget);
  }

  openCombinedSelect(selectTarget) {
    const select = $(selectTarget);
    window.requestAnimationFrame(() => {
      if (select.data('select2')) {
        select.select2('open');
        this.focusCombinedSelectWhileOpen(selectTarget);
      } else {
        selectTarget.focus();
      }
    });
  }

  focusCombinedSelectWhileOpen(selectTarget) {
    const wrapper = selectTarget.closest('.timeline-combined-select');
    const focusWhileOpen = () => {
      const searchField = wrapper?.querySelector('.select2-container--open .select2-search__field');
      if (!searchField) return;

      searchField.focus();
      window.requestAnimationFrame(focusWhileOpen);
    };

    focusWhileOpen();
  }

  handleCombinedSelectKeydown(event) {
    if (event.key !== 'Escape') return;

    const selectTarget = this.combinedSelectTargets.find((target) => {
      return $(target).data('select2') && $(target).select2('isOpen');
    });
    if (!selectTarget) return;

    this.consumeCombinedSelectEscapeKeyup = true;
    this.consumeKeyboardEvent(event);
    $(selectTarget).select2('close');
  }

  handleCombinedSelectKeyup(event) {
    if (event.key !== 'Escape' || !this.consumeCombinedSelectEscapeKeyup) return;

    this.consumeCombinedSelectEscapeKeyup = false;
    this.consumeKeyboardEvent(event);
  }

  consumeKeyboardEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  addTime(event) {
    if (event) event.preventDefault();
    if (!this.hasPrecisionSelectTarget) return;

    this.bufferExpanded = false;
    this.precisionExpanded = false;
    this.setSelectValue(this.precisionTarget, this.defaultTimedPrecisionValue);
    $(this.precisionTarget).trigger('change');
    this.syncTimelineRemovalState();
  }

  removeTime(event) {
    if (event) event.preventDefault();
    if (!this.hasPrecisionSelectTarget) return;

    this.bufferExpanded = false;
    this.precisionExpanded = false;
    this.setSelectValue(this.precisionTarget, this.defaultDayPrecisionValue);
    this.currentPrecisionInSeconds = this.getPrecisionSeconds();

    if (this.hasOffsetTarget) {
      this.populateOffsetOptions();
      this.setSelectValue(this.offsetTarget, 'PT0S');
    }
    if (this.hasDurationTarget) {
      this.populateDurationOptions(24 * 3600);
      this.setSelectValue(this.durationTarget, '1');
    }
    if (this.hasBufferTarget) {
      this.populateBufferOptions(0);
      this.setSelectValue(this.bufferTarget, '0');
    }

    this.updateCount();
    this.updateDetailsVisibility();
    this.syncTimelineRemovalState();
    this.renderPreview();
  }

  setSelectValue(selectTarget, value) {
    const select = $(selectTarget);
    if (value && !select.find(`option[value="${value}"]`).length) {
      select.append(new Option(value, value));
    }
    select.val(value).trigger('change.select2');
  }

  showDatePicker(event) {
    if (event) event.preventDefault();
    if (this.plugin) {
      this.plugin.show();
    } else if (this.hasFieldTarget) {
      this.fieldTarget.focus();
    }
  }

  toggleDate(event) {
    if (event) event.preventDefault();
    if (!this.usesDateToggle()) return this.showDatePicker(event);

    if (this.includeDateValue) {
      this.disableDate();
    } else {
      this.enableDate({showPicker: true});
    }
  }

  handleDateFieldClick(event) {
    if (!this.usesDateToggle() || this.includeDateValue) return;

    if (event) event.preventDefault();
    this.enableDate({showPicker: true});
  }

  enableDate({showPicker = false} = {}) {
    if (!this.usesDateToggle()) return;

    this.includeDateValue = true;
    const date = this.dateMomentForToggle();
    this.anchorTarget.value = date.format('YYYY-MM-DDTHH:mm:ss');
    this.fieldTarget.value = date.format(this.dateDisplayFormat());
    this.lastAnchorValue = this.anchorTarget.value;
    this.fieldTarget.setCustomValidity('');
    if (this.plugin) this.plugin.setStartDate(date);

    this.updateDateVisibility();
    this.syncTimelineRemovalState();
    this.renderPreview();
    if (showPicker) this.showDatePicker();
  }

  disableDate({render = true} = {}) {
    if (!this.usesDateToggle()) return;

    if (this.anchorTarget.value) this.lastAnchorValue = this.anchorTarget.value;
    this.includeDateValue = false;
    this.fieldTarget.value = '';
    this.anchorTarget.value = '';
    this.fieldTarget.setCustomValidity('');
    if (this.plugin) this.plugin.hide();

    this.updateDateVisibility();
    this.syncTimelineRemovalState();
    if (render) this.renderPreview();
  }

  dateMomentForToggle() {
    const anchorValue = this.anchorTarget.value || this.lastAnchorValue;
    if (anchorValue) {
      const anchorDate = moment(anchorValue, 'YYYY-MM-DDTHH:mm:ss');
      if (anchorDate.isValid()) return anchorDate;
    }

    return moment().startOf('day');
  }

  updateDateVisibility() {
    if (!this.usesDateToggle()) return;

    this.dateToggleTargets.forEach((target) => {
      target.classList.toggle('timeline-combined-button--active', this.includeDateValue);
      target.setAttribute('aria-pressed', this.includeDateValue ? 'true' : 'false');
    });

    this.dateControlTargets.forEach((target) => {
      target.classList.toggle('timeline-combined-date--inactive', !this.includeDateValue);
    });
  }

  usesDateToggle() {
    return this.hasDateToggleTarget && this.hasDateControlTarget && this.hasFieldTarget && this.hasAnchorTarget;
  }

  timelineHasActiveTime() {
    return this.hasPrecisionSelectTarget && !this.shouldHideDetails();
  }

  syncTimelineRemovalState() {
    if (!this.usesDateToggle()) return;

    const destroyField = this.destroyField;
    if (!destroyField) return;

    const shouldDestroy = !this.includeDateValue && !this.timelineHasActiveTime();
    destroyField.value = shouldDestroy ? '1' : '0';
    destroyField.disabled = false;
  }

  beforeSubmit() {
    this.syncTimelineRemovalState();
  }

  clearDate(event) {
    // don't submit the form, unless it originated from the cancel/clear button
    if (event) event.preventDefault()

    if (this.usesDateToggle()) {
      this.disableDate({render: false});
    } else {
      if (this.hasFieldTarget) {
        $(this.fieldTarget).val('')
        this.fieldTarget.setCustomValidity('')
      }
      if (this.hasAnchorTarget) {
        $(this.anchorTarget).val('')
      }
    }

    const destroyField = this.destroyField
    if (destroyField) {
      destroyField.value = "1"
      destroyField.disabled = false
    }
    this.renderPreview(); // Rerender preview on clear

    // Dispatch a generic event to notify the expandable-fields controller to collapse
    const expandableFieldsElement = this.element.closest('[data-controller="expandable-fields"]')
    if (expandableFieldsElement) {
      expandableFieldsElement.dispatchEvent(new CustomEvent('expandable-field:cleared'))
    }
  }

  applyTypedDate(event) {
    if (event?.type === 'keydown') {
      event.preventDefault();
    }
    if (!this.hasFieldTarget || !this.hasAnchorTarget) return;

    const value = this.fieldTarget.value.trim();
    if (value === '') {
      if (this.usesDateToggle()) {
        this.disableDate();
      } else {
        this.fieldTarget.setCustomValidity('');
        this.anchorTarget.value = '';
        this.renderPreview();
      }
      return;
    }

    const parsedDate = this.parseTypedDate(value);
    if (!parsedDate.isValid()) {
      this.fieldTarget.setCustomValidity(this.translationsValue.date?.invalid || 'Enter a valid date.');
      this.fieldTarget.reportValidity();
      return;
    }

    const normalizedDate = this.includeTimeValue ? parsedDate : parsedDate.clone().startOf('day');
    this.fieldTarget.setCustomValidity('');
    this.fieldTarget.value = normalizedDate.format(this.dateDisplayFormat());
    this.anchorTarget.value = normalizedDate.format('YYYY-MM-DDTHH:mm:ss');
    if (this.usesDateToggle()) {
      this.includeDateValue = true;
      this.lastAnchorValue = this.anchorTarget.value;
      this.updateDateVisibility();
      this.syncTimelineRemovalState();
    }
    if (this.plugin) this.plugin.setStartDate(normalizedDate);
    this.renderPreview();
  }

  applyDateToField(event, picker) {
    if(picker.startDate.isValid()) {
      $(this.fieldTarget).val(picker.startDate.format(this.dateDisplayFormat()))
      $(this.anchorTarget).val(picker.startDate.format('YYYY-MM-DDTHH:mm:ss'))
      if (this.usesDateToggle()) {
        this.includeDateValue = true;
        this.lastAnchorValue = this.anchorTarget.value;
        this.updateDateVisibility();
        this.syncTimelineRemovalState();
      }
    }
    else {
      if (this.usesDateToggle()) {
        this.disableDate({render: false});
      } else {
        $(this.fieldTarget).val('')
        $(this.anchorTarget).val('')
      }
    }
    this.fieldTarget.dispatchEvent(new CustomEvent('change', { detail: { picker: picker }}))
    this.renderPreview()
  }

  showTimeZoneButtons(event) {
    // don't follow the anchor
    if (event) event.preventDefault()

    $(this.currentTimeZoneWrapperTarget).toggleClass('hidden')
    $(this.timeZoneButtonsTarget).toggleClass('hidden')
  }

  showTimeZoneSelectWrapper(event) {
    // don't follow the anchor
    event.preventDefault()

    $(this.timeZoneButtonsTarget).toggleClass('hidden')

    if (this.hasTimeZoneSelectWrapperTarget) {
      $(this.timeZoneSelectWrapperTarget).toggleClass('hidden')
    }
  }

  resetTimeZoneUI(e) {
    e && e.preventDefault()

    $(this.currentTimeZoneWrapperTarget).removeClass('hidden')
    $(this.timeZoneButtonsTarget).addClass('hidden')

    if (this.hasTimeZoneSelectWrapperTarget) {
      $(this.timeZoneSelectWrapperTarget).addClass('hidden')
    }
  }

  updateTimeZone(value) {
    const currentTimeZoneEl = this.currentTimeZoneWrapperTarget.querySelector('a')

    $(this.timeZoneFieldTarget).val(value)
    $(currentTimeZoneEl).text(value)
    this.notifyTimelineChanged()
  }

  setTimeZone(event) {
    // don't follow the anchor
    event.preventDefault()

    const {value} = event.target.dataset
    this.updateTimeZone(value)

    $('.time-zone-button').removeClass('button').addClass('button-alternative')
    $(event.target).removeClass('button-alternative').addClass('button')

    this.resetTimeZoneUI()
  }

  dateDisplayFormat() {
    return this.includeTimeValue ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'
  }

  dateInputFormats() {
    if (this.includeTimeValue) {
      return [
        this.dateDisplayFormat(),
        'D.M.YYYY H:mm',
        'YYYY-MM-DD HH:mm',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm',
        moment.ISO_8601
      ];
    }

    return [
      this.dateDisplayFormat(),
      'D.M.YYYY',
      'YYYY-MM-DD',
      'YYYY/MM/DD',
      moment.ISO_8601
    ];
  }

  parseTypedDate(value) {
    return moment(value, this.dateInputFormats(), true);
  }

  precision() {
    const precisionValue = this.precisionTarget.value;
    const seconds = this.durationToSeconds(precisionValue);
    return moment.duration(seconds, 'seconds');
  }

  initPluginInstance() {
    if (this.hasFieldTarget) {
      const options = {
        singleDatePicker: true,
        timePicker: this.includeTimeValue,
        timePickerIncrement: 5,
        autoUpdateInput: false,
        autoApply: true,
        showWeekNumbers: true,
        locale: {
          cancelLabel: this.cancelButtonLabelValue,
          applyLabel: this.applyButtonLabelValue,
          format: this.dateDisplayFormat(),
          firstDay: this.firstDayOfWeek()
        }
      }

      const modalContent = this.fieldTarget.closest('dialog [data-modal-target~="content"]')
      if (modalContent) options.parentEl = modalContent

      const startsEmpty = this.anchorTarget.value === ''
      if(!startsEmpty){
        options.startDate = moment(this.anchorTarget.value, 'YYYY-MM-DDTHH:mm:ss')
      }

      $(this.fieldTarget).daterangepicker(options)

      if(startsEmpty){
        $(this.fieldTarget).val('')
        $(this.anchorTarget).val('')
      } else {
        $(this.fieldTarget).val(options.startDate.format(this.dateDisplayFormat()))
      }

      $(this.fieldTarget).on('apply.daterangepicker', this.applyDateToField.bind(this))
      $(this.fieldTarget).on('cancel.daterangepicker', this.clearDate.bind(this))
      $(this.fieldTarget).on('hide.daterangepicker', () => {
        if (!this.anchorTarget.value) $(this.fieldTarget).val('')
      })

      this.pluginMainEl = this.fieldTarget
      this.plugin = $(this.pluginMainEl).data('daterangepicker')

      if (document.activeElement === this.fieldTarget && this.plugin) {
        this.plugin.show()
      }
    }

    // Init time zone select
    if (this.includeTimeValue && this.hasTimeZoneSelectWrapperTarget) {
      this.timeZoneSelect = this.timeZoneSelectWrapperTarget.querySelector('select.select2')

      $(this.timeZoneSelect).select2({
        width: 'style'
      })

      const self = this

      $(this.timeZoneSelect).on('change.select2', function(event) {
        const {value} = event.target
        self.updateTimeZone(value)

        const selectedOptionTimeZoneButton = $('.selected-option-time-zone-button')

        if (self.defaultTimeZonesValue.includes(value)) {
          $('.time-zone-button').removeClass('button').addClass('button-alternative')
          selectedOptionTimeZoneButton.addClass('hidden').attr('hidden', true)
          $(`a[data-value="${value}"`).removeClass('button-alternative').addClass('button')
        } else {
          // deselect any selected button
          $('.time-zone-button').removeClass('button').addClass('button-alternative')

          selectedOptionTimeZoneButton.text(value)
          selectedOptionTimeZoneButton.attr('data-value', value).removeAttr('hidden')
          selectedOptionTimeZoneButton.removeClass(['hidden', 'button-alternative']).addClass('button')
        }

        self.resetTimeZoneUI()
      })
    }
  }

  teardownPluginInstance() {
    if (this.plugin === undefined) { return }

    $(this.pluginMainEl).off('apply.daterangepicker')
    $(this.pluginMainEl).off('cancel.daterangepicker')

    // revert to original markup, remove any event listeners
    this.plugin.remove()

    if (this.includeTimeValue) {
      $(this.timeZoneSelect).select2('destroy');
    }
  }

  formatTimeRange(startSeconds, precisionSeconds) {
    const startHours = Math.floor(startSeconds / 3600);
    const startMinutes = Math.floor((startSeconds % 3600) / 60);

    const endSeconds = startSeconds + precisionSeconds;
    const endHours = Math.floor(endSeconds / 3600);
    const endMinutes = Math.floor((endSeconds % 3600) / 60);

    const formatTime = (hours, minutes) => {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    return `${formatTime(startHours, startMinutes)} - ${formatTime(endHours, endMinutes)}`;
  }

  populateOffsetOptions() {
    const offsetSelect = $(this.offsetTarget);
    const originalValue = offsetSelect.val();
    const precisionSeconds = this.getPrecisionSeconds();

    if (precisionSeconds <= 0) {
      offsetSelect.empty(); // Clear options if precision is not valid
      return;
    }

    offsetSelect.empty();

    const bounds = this.offsetOptionBounds();
    const firstOptionSeconds = Math.ceil(bounds.startSeconds / precisionSeconds) * precisionSeconds;
    const offsetOptions = [];
    for (let seconds = firstOptionSeconds; seconds < bounds.endSeconds; seconds += precisionSeconds) {
      offsetOptions.push({
        seconds,
        text: this.formatOffsetOption(seconds, precisionSeconds),
        value: this.durationValueForSeconds(seconds)
      });
    }

    const textCounts = offsetOptions.reduce((counts, option) => {
      counts.set(option.text, (counts.get(option.text) || 0) + 1);
      return counts;
    }, new Map());

    offsetOptions.forEach(({seconds, text, value}) => {
      const optionText = textCounts.get(text) > 1
        ? this.formatOffsetOption(seconds, precisionSeconds, { includeZone: true })
        : text;
      offsetSelect.append(new Option(optionText, value));
    });

    if (originalValue && !offsetSelect.find(`option[value="${originalValue}"]`).length) {
      const currentOffsetSeconds = this.durationToSeconds(originalValue);
      if (!isNaN(currentOffsetSeconds)) {
        const optionText = this.formatOffsetOption(currentOffsetSeconds, precisionSeconds, { custom: true });
        offsetSelect.append(new Option(optionText, originalValue));
      }
    }

    if (originalValue) {
      offsetSelect.val(originalValue).trigger('change.select2');
    } else if (offsetSelect.find('option').length > 0) {
      offsetSelect.val(offsetSelect.find('option:first').val()).trigger('change.select2');
    }
  }

  populatePrecisionOptions() {
    const precisionSelect = $(this.precisionTarget);
    const originalValue = precisionSelect.val();

    precisionSelect.empty();

    // Use translations for standard precision options
    const translatedOptions = this.translationsValue.precision.options;
    for (const key in translatedOptions) {
      if (translatedOptions.hasOwnProperty(key)) {
        const text = translatedOptions[key];
        // Correction: Keys in YAML are '5', '10', etc. representing minutes. So convert to seconds for PTxS.
        const valueInSeconds = parseInt(key) * 60;
        const optionValue = `PT${valueInSeconds}S`;
        const option = new Option(text, optionValue);
        precisionSelect.append(option);
      }
    }

    if (originalValue && !precisionSelect.find(`option[value="${originalValue}"]`).length) {
      const seconds = this.durationToSeconds(originalValue);
      const minutes = Math.floor(seconds / 60);
      const optionText = this.translationsValue.precision.minutes.replace(/%\{count\}/g, minutes);
      const option = new Option(optionText, originalValue);
      precisionSelect.append(option);
    }

    if (originalValue) {
      precisionSelect.val(originalValue);
      precisionSelect.trigger('change');
    } else if (precisionSelect.find('option').length > 0) {
      precisionSelect.val(precisionSelect.find('option:first').val()).trigger('change.select2');
    }
  }

  formatDuration(count, precisionSeconds) {
    const totalSeconds = count * precisionSeconds;

    const formatUnit = (value, unit) => {
      if (value === 0) return '';
      return this.translationsValue.duration.units[unit].replace(/%\{count\}/g, value);
    };

    // Helper to format combined units
    const formatCombined = (parts) => {
      return parts.filter(Boolean).join(' ');
    };

    // Calculate all possible units
    const seconds = totalSeconds % 60;
    const minutes = Math.floor((totalSeconds / 60) % 60);
    const hours = Math.floor((totalSeconds / 3600) % 24);
    const days = Math.floor(totalSeconds / 86400);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(weeks / 4);
    const years = Math.floor(months / 12);

    // Choose format based on total duration
    if (totalSeconds === 0) {
      return this.translationsValue.duration.formats.zero;
    }

    // For very small durations (less than 1 hour)
    if (totalSeconds < 3600) {
      if (minutes === 0) {
        return formatUnit(seconds, 'seconds');
      }
      return formatUnit(minutes, 'minutes');
    }

    // For durations less than 1 day
    if (totalSeconds < 86400) {
      return formatCombined([
        formatUnit(hours, 'hours'),
        minutes > 0 ? formatUnit(minutes, 'minutes') : null
      ]);
    }

    // For durations less than 1 week
    if (totalSeconds < 604800) {
      return formatCombined([
        formatUnit(days, 'days'),
        hours > 0 ? formatUnit(hours, 'hours') : null
      ]);
    }

    // For durations less than 1 month
    if (totalSeconds < 2592000) {
      return formatCombined([
        formatUnit(weeks, 'weeks'),
        days % 7 > 0 ? formatUnit(days % 7, 'days') : null
      ]);
    }

    // For durations less than 1 year
    if (totalSeconds < 31536000) {
      return formatCombined([
        formatUnit(months, 'months'),
        weeks % 4 > 0 ? formatUnit(weeks % 4, 'weeks') : null
      ]);
    }

    // For durations of 1 year or more
    return formatCombined([
      formatUnit(years, 'years'),
      months % 12 > 0 ? formatUnit(months % 12, 'months') : null
    ]);
  }

  getPrecisionSeconds() {
    if (!this.hasPrecisionTarget) return 0;
    const precisionValue = $(this.precisionTarget).val();
    if (!precisionValue) return 0;
    return this.durationToSeconds(precisionValue);
  }

  populateDurationOptions(targetActualSeconds = null) {
    const durationSelect = $(this.durationTarget);
    const originalCountValue = durationSelect.val();
    const precisionSeconds = this.getPrecisionSeconds();
    const offsetStartSeconds = this.getOffsetStartSeconds();

    if (precisionSeconds <= 0) return durationSelect.empty();

    durationSelect.empty();
    let valueToSelect = originalCountValue;

    this.optionCountsForPrecision(precisionSeconds, {
      maxSeconds: 24 * 3600,
      tiers: [
        { until: 60 * 60, step: precisionSeconds },
        { from: 60 * 60, until: 4 * 3600, step: 15 * 60 },
        { from: 4 * 3600, until: 8 * 3600, step: 30 * 60 },
        { from: 8 * 3600, until: 12 * 3600, step: 60 * 60 },
        { from: 12 * 3600, until: 24 * 3600, step: 2 * 3600 }
      ]
    }).forEach((count) => {
      const formattedDurationText = this.formatDuration(count, precisionSeconds);
      const currentDurationSecondsForOption = count * precisionSeconds;
      const eventActualEndTimeSeconds = offsetStartSeconds + currentDurationSecondsForOption;

      const untilRange_EndTimeDisplay = this.formatTimeForDisplay(eventActualEndTimeSeconds);

      const untilText = this.translationsValue.common.until_range
        .replace(/%\{end_time\}/g, untilRange_EndTimeDisplay);

      const optionText = this.translationsValue.common.select_option_layout
        .replace(/%\{range_text\}/g, untilText)
        .replace(/%\{value_text\}/g, formattedDurationText);
      durationSelect.append(new Option(optionText, count.toString()));
    });

    if (originalCountValue && !durationSelect.find(`option[value="${originalCountValue}"]`).length) {
      const count = this.matchingOptionValue(durationSelect, originalCountValue) ? NaN : parseFloat(originalCountValue);
      if (!Number.isNaN(count)) {
        const formattedDurationText = this.formatDuration(count, precisionSeconds);
        const currentDurationSecondsForOption = count * precisionSeconds;
        const eventActualEndTimeSeconds = offsetStartSeconds + currentDurationSecondsForOption;

        const untilRange_EndTimeDisplay = this.formatTimeForDisplay(eventActualEndTimeSeconds);

        const untilText = this.translationsValue.common.until_range
          .replace(/%\{end_time\}/g, untilRange_EndTimeDisplay);

        const optionText = this.translationsValue.common.select_option_layout
          .replace(/%\{range_text\}/g, untilText)
          .replace(/%\{value_text\}/g, formattedDurationText);
        durationSelect.append(new Option(optionText, originalCountValue));
      }
    }

    if (targetActualSeconds !== null && precisionSeconds > 0) {
      let bestMatchOptionValue = null;
      let minDifference = Infinity;

      durationSelect.find('option').each(function() {
        const optionCount = parseFloat($(this).val());
        if (isNaN(optionCount)) return;

        const optionTimeInSeconds = optionCount * precisionSeconds;
        const difference = Math.abs(optionTimeInSeconds - targetActualSeconds);

        if (difference < minDifference) {
          minDifference = difference;
          bestMatchOptionValue = $(this).val();
        } else if (difference === minDifference) {
          // Optional: Prefer smaller count if time difference is identical
          if (optionCount < parseFloat(bestMatchOptionValue)) {
            bestMatchOptionValue = $(this).val();
          }
        }
      });
      if (bestMatchOptionValue !== null) {
        valueToSelect = bestMatchOptionValue;
      }
    } else {
      valueToSelect = this.matchingOptionValue(durationSelect, originalCountValue) || valueToSelect;
    }

    if (valueToSelect) {
      durationSelect.val(valueToSelect).trigger('change');
    } else if (durationSelect.find('option').length > 0) {
      durationSelect.val(durationSelect.find('option:first').val()).trigger('change');
    }
  }

  populateBufferOptions(targetActualSeconds = null) {
    const bufferSelect = $(this.bufferTarget);
    const originalCountValue = bufferSelect.val();
    const precisionSeconds = this.getPrecisionSeconds();
    const offsetStartSeconds = this.getOffsetStartSeconds();
    let selectedDurationCount = 0;
    if (this.hasDurationTarget) {
      selectedDurationCount = parseFloat($(this.durationTarget).val() || '0');
    }
    const selectedDurationSeconds = selectedDurationCount * precisionSeconds;
    if (precisionSeconds <= 0) return bufferSelect.empty();
    bufferSelect.empty();
    let valueToSelect = originalCountValue;

    // Zero buffer option
    const zeroBufferValueText = this.translationsValue.selects.buffer.zero;
    const eventActualEndTimeForZeroBuffer = offsetStartSeconds + selectedDurationSeconds; // Event ends after duration, before buffer

    const zeroUntilRange_EndTimeDisplay = this.formatTimeForDisplay(eventActualEndTimeForZeroBuffer);

    const zeroUntilText = (this.translationsValue.common.buffer_range || this.translationsValue.common.until_range)
        .replace(/%\{end_time\}/g, zeroUntilRange_EndTimeDisplay);

    const zeroBufferOptionText = this.translationsValue.common.select_option_layout
        .replace(/%\{range_text\}/g, zeroUntilText)
        .replace(/%\{value_text\}/g, zeroBufferValueText);
    const zeroBufferOption = new Option(zeroBufferOptionText, "0");
    zeroBufferOption.dataset.timelineSelectionLabel = zeroBufferValueText;
    bufferSelect.append(zeroBufferOption);

    this.optionCountsForPrecision(precisionSeconds, {
      maxSeconds: 12 * 3600,
      tiers: [
        { until: 30 * 60, step: precisionSeconds },
        { from: 30 * 60, until: 2 * 3600, step: 15 * 60 },
        { from: 2 * 3600, until: 6 * 3600, step: 30 * 60 },
        { from: 6 * 3600, until: 12 * 3600, step: 60 * 60 }
      ]
    }).forEach((count) => {
      const formattedBufferText = this.formatDuration(count, precisionSeconds);
      const currentBufferSecondsForOption = count * precisionSeconds;
      const eventActualEndTimeSeconds = offsetStartSeconds + selectedDurationSeconds + currentBufferSecondsForOption;

      const untilRange_EndTimeDisplay = this.formatTimeForDisplay(eventActualEndTimeSeconds);

      const untilText = (this.translationsValue.common.buffer_range || this.translationsValue.common.until_range)
        .replace(/%\{end_time\}/g, untilRange_EndTimeDisplay);

      const optionText = this.translationsValue.common.select_option_layout
        .replace(/%\{range_text\}/g, untilText)
        .replace(/%\{value_text\}/g, formattedBufferText);
      const option = new Option(optionText, count.toString());
      option.dataset.timelineSelectionLabel = this.bufferSelectionLabel(
        this.formatBufferSelectionValue(count, precisionSeconds)
      );
      bufferSelect.append(option);
    });

    if (originalCountValue && originalCountValue !== "0" && !bufferSelect.find(`option[value="${originalCountValue}"]`).length) {
      const count = this.matchingOptionValue(bufferSelect, originalCountValue) ? NaN : parseFloat(originalCountValue);
      if (!Number.isNaN(count)) {
        const formattedBufferText = this.formatDuration(count, precisionSeconds);
        const currentBufferSecondsForOption = count * precisionSeconds;
        const eventActualEndTimeSeconds = offsetStartSeconds + selectedDurationSeconds + currentBufferSecondsForOption;

        const untilRange_EndTimeDisplay = this.formatTimeForDisplay(eventActualEndTimeSeconds);

        const untilText = (this.translationsValue.common.buffer_range || this.translationsValue.common.until_range)
          .replace(/%\{end_time\}/g, untilRange_EndTimeDisplay);

        const optionText = this.translationsValue.common.select_option_layout
          .replace(/%\{range_text\}/g, untilText)
          .replace(/%\{value_text\}/g, formattedBufferText);
        const option = new Option(optionText, originalCountValue);
        option.dataset.timelineSelectionLabel = this.bufferSelectionLabel(
          this.formatBufferSelectionValue(count, precisionSeconds)
        );
        bufferSelect.append(option);
      }
    }

    if (targetActualSeconds !== null && precisionSeconds > 0) {
      let bestMatchOptionValue = null;
      let minDifference = Infinity;
      // Check zero option first if target is zero
      if (targetActualSeconds === 0) {
        bestMatchOptionValue = "0";
      } else {
        bufferSelect.find('option').each(function() {
          const optionCount = parseFloat($(this).val());
          if (isNaN(optionCount) || optionCount === 0) return; // Skip zero here, handled above

          const optionTimeInSeconds = optionCount * precisionSeconds;
          const difference = Math.abs(optionTimeInSeconds - targetActualSeconds);

          if (difference < minDifference) {
            minDifference = difference;
            bestMatchOptionValue = $(this).val();
          } else if (difference === minDifference) {
            if (optionCount < parseFloat(bestMatchOptionValue)) {
              bestMatchOptionValue = $(this).val();
            }
          }
        });
      }
      if (bestMatchOptionValue !== null) {
        valueToSelect = bestMatchOptionValue;
      }
    } else if (targetActualSeconds === 0) { // Handle case where target is 0, select "0"
        valueToSelect = "0";
    } else {
      valueToSelect = this.matchingOptionValue(bufferSelect, originalCountValue) || valueToSelect;
    }

    if (valueToSelect) {
      bufferSelect.val(valueToSelect).trigger('change');
    } else if (bufferSelect.find('option').length > 0) {
      bufferSelect.val(bufferSelect.find('option:first').val()).trigger('change');
    }
  }

  handleOffsetChange() {
    if (this.hasDurationTarget) this.populateDurationOptions();
    if (this.hasBufferTarget) this.populateBufferOptions();
  }

  getOffsetStartSeconds() {
    if (!this.hasOffsetTarget) return 0;
    const offsetValue = $(this.offsetTarget).val();
    if (!offsetValue) return 0;
    return this.durationToSeconds(offsetValue);
  }

  offsetOptionBounds() {
    const daySeconds = 24 * 3600;
    if (this.usesWallClockReference()) {
      if (this.shouldBoundOffsetsToParentWindow()) {
        const referenceMilliseconds = Date.parse(this.parentReferenceUtcValue || '');
        const startMilliseconds = Date.parse(this.parentTotalStartUtcValue || '');
        const endMilliseconds = Date.parse(this.parentTotalEndUtcValue || '');

        if (
          !Number.isNaN(referenceMilliseconds) &&
          !Number.isNaN(startMilliseconds) &&
          !Number.isNaN(endMilliseconds) &&
          endMilliseconds > startMilliseconds
        ) {
          return {
            startSeconds: Math.floor((startMilliseconds - referenceMilliseconds) / 1000),
            endSeconds: Math.ceil((endMilliseconds - referenceMilliseconds) / 1000)
          };
        }
      }

      const wallClockReferenceSeconds = this.getWallClockReferenceSeconds();
      return {
        startSeconds: -wallClockReferenceSeconds,
        endSeconds: daySeconds - wallClockReferenceSeconds
      };
    }

    return { startSeconds: 0, endSeconds: daySeconds };
  }

  shouldBoundOffsetsToParentWindow() {
    return this.hasParentPointValue && this.parentPointValue !== 'anchor';
  }

  usesWallClockReference() {
    return this.hasWallClockReferenceValue && this.wallClockReferenceValue !== '';
  }

  parentReferenceAvailable() {
    return this.hasParentReferenceUtcValue &&
      this.parentReferenceUtcValue &&
      this.hasParentTimeZoneValue &&
      this.parentTimeZoneValue;
  }

  getWallClockReferenceSeconds() {
    if (this.usesWallClockReference()) return this.durationToSeconds(this.wallClockReferenceValue);
    if (this.hasWallClockReferenceTarget) return this.durationToSeconds(this.wallClockReferenceTarget.value);
    return 0;
  }

  formatTimeForDisplay(totalSeconds, { includeZone = false } = {}) {
    if (isNaN(totalSeconds)) return "--:--";
    if (this.parentReferenceAvailable()) {
      const parentRelativeDate = this.dateFromParentReference(totalSeconds);
      if (parentRelativeDate) return this.formatDateInTimeZone(parentRelativeDate, this.parentTimeZoneValue, { includeZone });
    }

    return moment.utc(this.displaySecondsForWallClockOffset(totalSeconds) * 1000).format(this.translationsValue.selects.offset.time_format);
  }

  displaySecondsForWallClockOffset(totalSeconds) {
    if (this.usesWallClockReference()) {
      return this.secondsWithinDay(totalSeconds + this.getWallClockReferenceSeconds());
    }

    return this.secondsWithinDay(totalSeconds);
  }

  formatOffsetOption(startSeconds, precisionSeconds, { custom = false, includeZone = false } = {}) {
    const startTime = this.formatTimeForDisplay(startSeconds, { includeZone });
    const endTime = this.formatTimeForDisplay(startSeconds + precisionSeconds, { includeZone });
    const template = custom
      ? this.translationsValue.selects.offset.custom_option
      : this.translationsValue.selects.offset.option;

    return (template || "%{start_time}")
      .replace(/%\{start_time\}/g, startTime)
      .replace(/%\{end_time\}/g, endTime)
      .replace(/%\{time\}/g, startTime);
  }

  dateFromParentReference(offsetSeconds) {
    const parentReferenceMilliseconds = Date.parse(this.parentReferenceUtcValue);
    if (Number.isNaN(parentReferenceMilliseconds)) return null;

    return new Date(parentReferenceMilliseconds + (offsetSeconds * 1000));
  }

  formatDateInTimeZone(date, timeZone, { includeZone = false } = {}) {
    const locale = document.documentElement.lang || undefined;
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
        ...(includeZone ? { timeZoneName: 'short' } : {})
      });
    } catch (_error) {
      return moment.utc(date).format(this.translationsValue.selects.offset.time_format);
    }

    if (includeZone) return formatter.format(date);

    const parts = formatter.formatToParts(date);
    const hour = parts.find((part) => part.type === 'hour')?.value || '--';
    const minute = parts.find((part) => part.type === 'minute')?.value || '--';

    return `${hour}:${minute}`;
  }

  optionCountsForPrecision(precisionSeconds, { maxSeconds, tiers }) {
    if (precisionSeconds <= 0) return [];

    const desiredSeconds = new Set();
    tiers.forEach(({from = 0, until, step}) => {
      if (!until || !step) return;

      const firstSecond = Math.max(
        precisionSeconds,
        Math.floor(from / step) * step + step
      );

      for (let seconds = firstSecond; seconds <= until; seconds += step) {
        desiredSeconds.add(seconds);
      }
    });
    desiredSeconds.add(maxSeconds);

    return Array.from(desiredSeconds)
      .filter((seconds) => seconds > 0 && seconds <= maxSeconds)
      .filter((seconds) => seconds % precisionSeconds === 0)
      .sort((a, b) => a - b)
      .map((seconds) => seconds / precisionSeconds);
  }

  matchingOptionValue(select, value) {
    if (!value) return null;
    if (select.find(`option[value="${value}"]`).length) return value;

    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) return null;

    const matchingOption = Array.from(select[0]?.options || [])
      .find((option) => parseFloat(option.value) === numericValue);

    return matchingOption?.value || null;
  }

  _parsePTxSToSeconds(ptString) {
    return this.durationToSeconds(ptString);
  }

  durationValueForSeconds(seconds) {
    return `PT${seconds}S`;
  }

  durationToSeconds(value) {
    if (!value) return 0;

    let normalized = value.toString().trim();
    let sign = 1;
    if (normalized.startsWith('-')) {
      sign = -1;
      normalized = normalized.slice(1);
    }

    const match = normalized.match(/^P(?:(-?\d+(?:\.\d+)?)D)?(?:T(?:(-?\d+(?:\.\d+)?)H)?(?:(-?\d+(?:\.\d+)?)M)?(?:(-?\d+(?:\.\d+)?)S)?)?$/);
    if (!match) return 0;

    const days = parseFloat(match[1] || '0');
    const hours = parseFloat(match[2] || '0');
    const minutes = parseFloat(match[3] || '0');
    const seconds = parseFloat(match[4] || '0');

    return sign * ((days * 24 * 3600) + (hours * 3600) + (minutes * 60) + seconds);
  }

  _parseCountStringToEnd(countString) {
    if (!countString) return 0;
    try {
      const arr = JSON.parse(countString);
      return Array.isArray(arr) && arr.length > 1 ? parseFloat(arr[1]) || 0 : 0;
    } catch (e) {
      return 0;
    }
  }

  previewTranslation(key, fallback) {
    return (this.translationsValue && this.translationsValue.preview && this.translationsValue.preview[key]) || fallback;
  }

  secondsWithinDay(seconds, totalDaySeconds = 24 * 60 * 60) {
    return ((seconds % totalDaySeconds) + totalDaySeconds) % totalDaySeconds;
  }

  previewStartSeconds(totalDaySeconds) {
    if (this.usesWallClockReference()) {
      return this.getOffsetStartSeconds();
    }

    let startSeconds = 0;

    if (this.hasParentAnchorValue && this.parentAnchorValue) {
      const parentOffsetSec = this._parsePTxSToSeconds(this.hasParentOffsetValue ? this.parentOffsetValue : 'PT0S');
      startSeconds += parentOffsetSec;

      if (this.hasParentPointValue && this.parentPointValue === 'end') {
        const parentPrecisionSec = this._parsePTxSToSeconds(this.hasParentPrecisionValue ? this.parentPrecisionValue : 'PT0S');
        const parentDurationCountNum = this._parseCountStringToEnd(this.hasParentCountValue ? this.parentCountValue : '[0,0]');
        startSeconds += parentDurationCountNum * parentPrecisionSec;
      }
    }

    startSeconds += this.getOffsetStartSeconds();
    return this.secondsWithinDay(startSeconds, totalDaySeconds);
  }

  previewSnapSeconds(targetSpanSeconds) {
    if (targetSpanSeconds <= 4 * 3600) return 30 * 60;
    if (targetSpanSeconds <= 8 * 3600) return 3600;
    if (targetSpanSeconds <= 12 * 3600) return 2 * 3600;
    return 6 * 3600;
  }

  previewTickSeconds(rangeSpanSeconds) {
    if (rangeSpanSeconds <= 3 * 3600) return 30 * 60;
    if (rangeSpanSeconds <= 8 * 3600) return 3600;
    if (rangeSpanSeconds <= 12 * 3600) return 2 * 3600;
    if (rangeSpanSeconds <= 18 * 3600) return 3 * 3600;
    return 6 * 3600;
  }

  previewRange(eventStartSeconds, selectedEndSeconds, precisionSeconds) {
    const selectedSpanSeconds = Math.max(selectedEndSeconds - eventStartSeconds, precisionSeconds, 30 * 60);
    const paddingSeconds = Math.max(30 * 60, Math.min(3600, selectedSpanSeconds * 0.25));
    let targetSpanSeconds = selectedSpanSeconds + (paddingSeconds * 2);

    if (selectedSpanSeconds <= 2 * 3600) {
      targetSpanSeconds = Math.max(targetSpanSeconds, 4 * 3600);
    } else if (selectedSpanSeconds <= 4 * 3600) {
      targetSpanSeconds = Math.max(targetSpanSeconds, 5 * 3600);
    } else {
      targetSpanSeconds = Math.max(targetSpanSeconds, selectedSpanSeconds + (2 * 3600));
    }

    targetSpanSeconds = Math.min(targetSpanSeconds, Math.max(selectedSpanSeconds, 12 * 3600));

    const centerSeconds = (eventStartSeconds + selectedEndSeconds) / 2;
    const snapSeconds = this.previewSnapSeconds(targetSpanSeconds);
    const rangeStartSeconds = Math.floor((centerSeconds - (targetSpanSeconds / 2)) / snapSeconds) * snapSeconds;
    const rangeEndSeconds = Math.ceil((centerSeconds + (targetSpanSeconds / 2)) / snapSeconds) * snapSeconds;

    return {
      startSeconds: rangeStartSeconds,
      endSeconds: rangeEndSeconds,
      spanSeconds: rangeEndSeconds - rangeStartSeconds
    };
  }

  appendPreviewTick(axis, range, seconds) {
    if (seconds < range.startSeconds || seconds > range.endSeconds) return;

    const positionPercentage = ((seconds - range.startSeconds) / range.spanSeconds) * 100;
    const tick = document.createElement('div');
    tick.className = 'timeline-preview-tick';
    if (positionPercentage <= 0) tick.classList.add('start');
    if (positionPercentage >= 100) tick.classList.add('end');
    tick.style.left = `${positionPercentage}%`;

    const label = document.createElement('span');
    label.className = 'timeline-preview-tick-label';
    label.textContent = this.formatTimeForDisplay(seconds);

    tick.appendChild(label);
    axis.appendChild(tick);
  }

  appendPreviewGridLine(track, range, seconds) {
    if (seconds < range.startSeconds || seconds > range.endSeconds) return;

    const line = document.createElement('div');
    line.className = 'timeline-preview-grid-line';
    line.style.left = `${((seconds - range.startSeconds) / range.spanSeconds) * 100}%`;
    track.appendChild(line);
  }

  appendPreviewSegment(track, range, {startSeconds, durationSeconds, className, label, valueText}) {
    if (durationSeconds <= 0 || range.spanSeconds <= 0) return;

    const segmentStartSeconds = Math.max(startSeconds, range.startSeconds);
    const segmentEndSeconds = Math.min(startSeconds + durationSeconds, range.endSeconds);
    if (segmentEndSeconds <= segmentStartSeconds) return;

    const leftPercentage = ((segmentStartSeconds - range.startSeconds) / range.spanSeconds) * 100;
    const widthPercentage = ((segmentEndSeconds - segmentStartSeconds) / range.spanSeconds) * 100;
    const segment = document.createElement('div');
    segment.className = `timeline-preview-segment ${className}`;
    segment.style.left = `${leftPercentage}%`;
    segment.style.width = `${widthPercentage}%`;
    segment.title = `${label}: ${valueText} (${this.formatTimeForDisplay(startSeconds)} - ${this.formatTimeForDisplay(startSeconds + durationSeconds)})`;
    segment.textContent = widthPercentage >= 9 ? valueText : '';
    track.appendChild(segment);
  }

  notifyTimelineChanged() {
    this.element.dispatchEvent(new CustomEvent('timeline:changed', {bubbles: true}));
  }

  renderPreview() {
    this.notifyTimelineChanged();
    if (!this.hasPreviewTarget) return;

    this.previewTarget.innerHTML = '';
    this.previewTarget.className = 'timeline-preview';

    const destroyField = this.element.querySelector('input[name$="[_destroy]"]');
    if (destroyField && destroyField.value === '1') return;

    const currentPrecisionVal = this.getPrecisionSeconds();
    if (currentPrecisionVal <= 0) return;

    const totalDaySeconds = 24 * 60 * 60;
    const eventText = this.previewTranslation('event_label', 'Duration');
    const bufferText = this.previewTranslation('buffer_label', 'Buffer');

    const eventStartSeconds = this.previewStartSeconds(totalDaySeconds);
    const rawDurationVal = this.hasDurationTarget ? $(this.durationTarget).val() : '0';
    const currentDurationCountVal = parseFloat(rawDurationVal || '0');
    const currentDurationVal = currentDurationCountVal * currentPrecisionVal;

    const rawBufferVal = this.hasBufferTarget ? $(this.bufferTarget).val() : '0';
    const currentBufferCountVal = parseFloat(rawBufferVal || '0');
    const currentBufferVal = currentBufferCountVal * currentPrecisionVal;

    const eventEndSeconds = eventStartSeconds + currentDurationVal;
    const bufferStartSeconds = eventEndSeconds;
    const bufferEndSeconds = bufferStartSeconds + currentBufferVal;
    const selectedEndSeconds = Math.max(eventEndSeconds, bufferEndSeconds, eventStartSeconds + currentPrecisionVal);
    const range = this.previewRange(eventStartSeconds, selectedEndSeconds, currentPrecisionVal);

    const axis = document.createElement('div');
    axis.className = 'timeline-preview-axis';

    const track = document.createElement('div');
    track.className = 'timeline-preview-track';

    const tickSeconds = this.previewTickSeconds(range.spanSeconds);
    const firstTickSeconds = Math.ceil(range.startSeconds / tickSeconds) * tickSeconds;
    for (let tickSecond = firstTickSeconds; tickSecond <= range.endSeconds; tickSecond += tickSeconds) {
      this.appendPreviewTick(axis, range, tickSecond);
      this.appendPreviewGridLine(track, range, tickSecond);
    }

    this.appendPreviewSegment(track, range, {
      startSeconds: eventStartSeconds,
      className: 'segment-duration',
      label: eventText,
      valueText: this.formatDuration(currentDurationCountVal, currentPrecisionVal),
      durationSeconds: currentDurationVal
    });
    this.appendPreviewSegment(track, range, {
      startSeconds: bufferStartSeconds,
      className: 'segment-buffer',
      label: bufferText,
      valueText: this.formatDuration(currentBufferCountVal, currentPrecisionVal),
      durationSeconds: currentBufferVal
    });

    this.previewTarget.appendChild(axis);
    this.previewTarget.appendChild(track);
  }

  firstDayOfWeek() {
    const configuredFirstDay = this.translationsValue?.calendar?.first_day;
    const firstDay = Number.parseInt(configuredFirstDay, 10);

    return Number.isInteger(firstDay) ? firstDay : 0;
  }

  get destroyField() {
    return this.element.querySelector('input[name$="[_destroy]"]');
  }
}
